import React, { useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import LeaveMultiDatePicker from "./LeaveMultiDatePicker";
import { EXCUSE_REASON_CATEGORIES } from "./leaveConstants";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	DatePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Space,
	Statistic,
	Row,
	Col,
	Modal,
	Table,
	Tag,
	Layout,
} from "antd";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Option } = Select;
const { TextArea } = Input;
const { Content } = Layout;

const ApplyForLeave = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [userProfile, setUserProfile] = useState(null);
	const [leaveBalance, setLeaveBalance] = useState(null);
	const [leaveType, setLeaveType] = useState(null);
	const [additionalUsersModalVisible, setAdditionalUsersModalVisible] =
		useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState(
		[],
	);
	const [searchText, setSearchText] = useState("");
	const [filteredUsers, setFilteredUsers] = useState([]);

	useEffect(() => {
		message.config({ top: 150 });
		return () => {
			message.config({ top: 8 });
		};
	}, []);

	useEffect(() => {
		fetchUserProfile();
		fetchLeaveBalance();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
	}, []);

	const fetchUserProfile = async () => {
		try {
			const profile = await getCurrentUserProfile();
			setUserProfile(profile);

			// 직분 설정
			if (profile.classification_name) {
				form.setFieldsValue({
					name: profile.name,
					position:
						profile.classification_name ===
						"교역자"
							? "교역자"
							: "교직원",
				});
			}
		} catch (error) {
			message.error("사용자 정보를 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const fetchLeaveBalance = async () => {
		try {
			const response = await useAxios.get(
				"/utils/leave/balance/",
			);
			if (response.status === 200) {
				setLeaveBalance(response.data);
			}
		} catch (error) {
			console.error(
				"월차 잔액을 불러오는데 실패했습니다:",
				error,
			);
		}
	};

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const formData = {
				leave_type: values.leave_type,
				reason: values.reason || null,
				additional_users: values.additional_users || [],
			};
			if (values.leave_type === "사유서") {
				formData.reason_category = values.reason_category;
				const dates = (
					values.leave_dates || []
				).slice();
				if (!dates.length) {
					message.error(
						"사유서 대상 날짜를 하나 이상 선택해주세요.",
					);
					return;
				}
				formData.leave_dates = dates
					.sort(
						(a, b) =>
							a.valueOf() -
							b.valueOf(),
					)
					.map((d) => d.format("YYYY-MM-DD"));
			} else {
				formData.leave_date =
					values.leave_date.format("YYYY-MM-DD");
			}

			const response = await useAxios.post(
				"/utils/leave/apply/",
				formData,
			);

			if (response.status === 201) {
				// 사유서를 작성했을 때는 다른 메시지가 떠야 함
				if (values.leave_type === "사유서") {
					message.success(
						"사유서가 접수되었습니다.",
					);
				} else if (values.leave_type === "월차") {
					message.success(
						"연차로 신청되었습니다.",
					);
				} else {
					message.success(
						"휴가 신청이 완료되었습니다.",
					);
				}
				form.resetFields();
				setSelectedAdditionalUsers([]);
				// 직분 다시 설정
				if (userProfile?.classification_name) {
					form.setFieldsValue({
						name: userProfile.name,
						position:
							userProfile.classification_name ===
							"교역자"
								? "교역자"
								: "교직원",
					});
				}
				fetchLeaveBalance();
			}
		} catch (error) {
			if (error.response && error.response.data.error) {
				console.error(error.response.data);
				message.error(error.response.data.error);
			} else {
				message.error(
					"신청 처리 중 오류가 발생했습니다.",
				);
				console.error(error);
			}
		} finally {
			setLoading(false);
		}
	};

	const disabledDate = (current) => {
		if (current && current.day() === 0) {
			return true;
		}
		// 사유서·월차·생휴는 오늘 이전 날짜도 선택 가능
		if (
			leaveType === "사유서" ||
			leaveType === "월차" ||
			leaveType === "생휴"
		) {
			return false;
		}
		return current && current < dayjs().startOf("day");
	};

	const handleLeaveTypeChange = (value) => {
		setLeaveType(value);
		if (value === "사유서") {
			form.setFieldsValue({
				reason: undefined,
				reason_category: undefined,
				additional_users: undefined,
				leave_date: undefined,
				leave_dates: [],
			});
			setSelectedAdditionalUsers([]);
			return;
		}
		form.setFieldsValue({
			reason: undefined,
			reason_category: undefined,
			additional_users: undefined,
			leave_dates: undefined,
			leave_date: undefined,
		});
		setSelectedAdditionalUsers([]);
	};

	const fetchAvailableUsers = async () => {
		try {
			const response = await useAxios.get(
				"/users/clergy-staff/",
			);
			if (response.status === 200) {
				setAvailableUsers(response.data);
				setFilteredUsers(response.data);
			}
		} catch (error) {
			message.error("사용자 목록을 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	useEffect(() => {
		if (additionalUsersModalVisible) {
			fetchAvailableUsers();
		}
	}, [additionalUsersModalVisible]);

	useEffect(() => {
		if (!searchText) {
			setFilteredUsers(availableUsers);
			return;
		}
		const filtered = availableUsers.filter(
			(user) =>
				user.name
					.toLowerCase()
					.includes(searchText.toLowerCase()) ||
				(user.classification_name &&
					user.classification_name
						.toLowerCase()
						.includes(
							searchText.toLowerCase(),
						)),
		);
		setFilteredUsers(filtered);
	}, [searchText, availableUsers]);

	const handleOpenAdditionalUsersModal = () => {
		setAdditionalUsersModalVisible(true);
		setSearchText("");
	};

	const handleSelectAdditionalUsers = () => {
		form.setFieldsValue({
			additional_users: selectedAdditionalUsers.map(
				(u) => u.id,
			),
		});
		setAdditionalUsersModalVisible(false);
	};

	const getJobLabel = (job, gender) => {
		if (job === "목사") {
			return "목사";
		} else if (job === "전도사") {
			return gender === "남" ? "남전도사" : "여전도사";
		} else if (job === "교직원") {
			return gender === "남" ? "남직원" : "여직원";
		}
		return job || "-";
	};

	const additionalUsersColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "직분",
			dataIndex: "classification_name",
			key: "classification_name",
		},
	];

	const rowSelection = {
		selectedRowKeys: selectedAdditionalUsers.map((u) => u.id),
		preserveSelectedRowKeys: true,
		onChange: (selectedRowKeys) => {
			setSelectedAdditionalUsers((prev) => {
				const byId = new Map(
					availableUsers.map((u) => [u.id, u]),
				);
				return selectedRowKeys
					.map(
						(id) =>
							byId.get(id) ??
							prev.find(
								(u) =>
									u.id ===
									id,
							),
					)
					.filter(Boolean);
			});
		},
	};

	const isFemale = userProfile?.gender === "여";

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="월차ㆍ사유서 신청" />
			<Layout style={{ minHeight: "100vh" }}>
				<LeaveNavigation />
				<Layout>
					<Content className="app-page-content">
						<div className="utils-rent_sanctuary__container page">
							<PageHeader title="월차ㆍ사유서 신청" />
							<div className="utils-rent_sanctuary__content-container">
								{leaveBalance &&
									isFemale && (
									<Card
										className="surface-card"
										style={{
											marginBottom: 24,
										}}>
										<Row
											gutter={[
												24,
												0,
											]}
											justify="start">
											<Col flex="auto">
												<Statistic
													title="이번 달 남은 생리휴가"
													value={
														leaveBalance.monthly_menstrual_leave_balance
													}
													suffix="개"
												/>
											</Col>
										</Row>
									</Card>
								)}
								<Card className="utils-rent_sanctuary__form-card">
									<Space
										direction="vertical"
										style={{
											width: "100%",
										}}>
										<h3
											style={{
												fontSize: 14,
												fontWeight: 600,
												marginBottom: 30,
											}}>
											✅
											사이트
											이용이
											어려우신
											경우,
											기존과
											같이
											종이로
											작성하실
											수
											있습니다.
											<br />
											✅
											사유서
											제출
											시
											동행인도
											추가
											가능합니다.
											<br />
											✅
											연차
											및
											출장품의서는
											결재가
											필요한
											사항으로,
											기존과
											동일하게
											종이로
											제출해주시기
											바랍니다.
											<br />

											✅
											신청하신
											내용은
											‘내
											신청
											이력’
											페이지에서
											확인하실
											수
											있습니다.
											<br />
											✅
											월차는
											연차로
											변환되어
											신청됩니다.
										</h3>
									</Space>
									<Form
										form={
											form
										}
										layout="vertical"
										onFinish={
											onFinish
										}
										initialValues={{
											application_date:
												dayjs(),
										}}>
										<Form.Item
											name="name"
											label="이름">
											<Input
												disabled
												placeholder="자동 입력"
											/>
										</Form.Item>

										<Form.Item
											name="position"
											label="직분">
											<Select
												disabled
												placeholder="자동 입력">
												<Option value="교역자">
													교역자
												</Option>
												<Option value="교직원">
													교직원
												</Option>
											</Select>
										</Form.Item>

										<Form.Item
											name="leave_type"
											label="구분"
											rules={[
												{
													required: true,
													message: "구분을 선택해주세요",
												},
											]}>
											<Select
												placeholder="구분을 선택하세요"
												onChange={
													handleLeaveTypeChange
												}>
												<Option value="월차">
													월차
												</Option>
												{isFemale && (
													<Option value="생휴">
														생리휴가
													</Option>
												)}
												<Option value="사유서">
													사유서
												</Option>
											</Select>
										</Form.Item>
										{leaveType ===
										"월차" ? (
											<div
												style={{
													marginTop: -8,
													marginBottom: 16,
													color: "rgba(0,0,0,0.65)",
													fontSize: 13,
												}}>
												월차는
												연차로
												변환되어
												신청됩니다.
												월
												1회
												제한은
												적용되지
												않습니다.
											</div>
										) : null}

										<Form.Item
											name="application_date"
											label="신청일">
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												disabled
												format="YYYY-MM-DD"
											/>
										</Form.Item>

										{leaveType !==
											"사유서" && (
											<Form.Item
												name="leave_date"
												label="사용일"
												rules={[
													{
														required: true,
														message: "사용일을 선택해주세요",
													},
												]}>
												<DatePicker
													inputReadOnly={isMobile}
													style={{
														width: "100%",
													}}
													disabledDate={
														disabledDate
													}
													format="YYYY-MM-DD"
													placeholder="사용일을 선택하세요"
												/>
											</Form.Item>
										)}
										{leaveType ===
											"사유서" && (
											<Form.Item
												name="leave_dates"
												label="대상 날짜"
												rules={[
													{
														required: true,
														message: "날짜를 하나 이상 선택해주세요.",
													},
													{
														validator: (
															_,
															v,
														) =>
															Array.isArray(
																v,
															) &&
															v.length >
																0
																? Promise.resolve()
																: Promise.reject(
																		new Error(
																			"날짜를 하나 이상 선택해주세요.",
																		),
																	),
													},
												]}>
												<LeaveMultiDatePicker
													disabledDate={
														disabledDate
													}
												/>
											</Form.Item>
										)}
										{leaveType ===
											"사유서" && (
											<>
												<Form.Item
													name="reason_category"
													label="세부분류"
													rules={[
														{
															required: true,
															message: "세부분류를 선택해주세요",
														},
													]}>
													<Select placeholder="세부분류를 선택하세요">
														{EXCUSE_REASON_CATEGORIES.map(
															(
																cat,
															) => (
																<Option
																	key={
																		cat
																	}
																	value={
																		cat
																	}>
																	{
																		cat
																	}
																</Option>
															),
														)}
													</Select>
												</Form.Item>
												<Form.Item
													name="reason"
													label="사유"
													rules={[
														{
															required: true,
															message: "사유를 입력해주세요",
														},
													]}>
													<TextArea
														rows={
															4
														}
														placeholder="사유를 입력하세요"
													/>
												</Form.Item>
												<Form.Item
													name="additional_users"
													label="추가 해당인">
													<Space
														direction="vertical"
														style={{
															width: "100%",
														}}>
														<Button
															type="dashed"
															onClick={
																handleOpenAdditionalUsersModal
															}
															block
															icon={
																<UserOutlined />
															}>
															추가
															해당인
															선택
														</Button>
														{selectedAdditionalUsers.length >
															0 && (
															<div>
																{selectedAdditionalUsers.map(
																	(
																		user,
																	) => (
																		<Tag
																			key={
																				user.id
																			}
																			closable
																			onClose={() => {
																				const newSelected =
																					selectedAdditionalUsers.filter(
																						(
																							u,
																						) =>
																							u.id !==
																							user.id,
																					);
																				setSelectedAdditionalUsers(
																					newSelected,
																				);
																				form.setFieldsValue(
																					{
																						additional_users:
																							newSelected.map(
																								(
																									u,
																								) =>
																									u.id,
																							),
																					},
																				);
																			}}
																			style={{
																				marginBottom: 8,
																			}}>
																			{
																				user.name
																			}{" "}
																			(
																			{getJobLabel(
																				user.job,
																				user.gender,
																			)}

																			)
																		</Tag>
																	),
																)}
															</div>
														)}
													</Space>
												</Form.Item>
											</>
										)}

										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												loading={
													loading
												}
												block>
												신청하기
											</Button>
										</Form.Item>
									</Form>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
			<Modal
				title="추가 해당인 선택"
				open={additionalUsersModalVisible}
				onOk={handleSelectAdditionalUsers}
				onCancel={() => {
					setAdditionalUsersModalVisible(false);
					setSearchText("");
				}}
				width={isMobile ? "100%" : 720}
				okText="선택 완료"
				cancelText="취소">
				<Space
					direction="vertical"
					style={{ width: "100%" }}
					size="large">
					<Input
						placeholder="이름 또는 직분으로 검색"
						prefix={<SearchOutlined />}
						value={searchText}
						onChange={(e) =>
							setSearchText(
								e.target.value,
							)
						}
					/>
					<Table
						rowSelection={rowSelection}
						columns={additionalUsersColumns}
						dataSource={filteredUsers}
						rowKey="id"
						pagination={{
							pageSize: 10,
							showSizeChanger: true,
						}}
					/>
				</Space>
			</Modal>
		</ConfigProvider>
	);
};

export default ApplyForLeave;
